import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ actual: '', nueva: '', confirmar: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  // Open sidebar by default on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { path: '/admin', label: 'Dashboard', icon: '📊', adminOnly: true },
    { path: '/admin/jugadores', label: 'Jugadores', icon: '⚽', adminOnly: true },
    { path: '/admin/representantes', label: 'Representantes', icon: '👨‍👩‍👦', adminOnly: true },
    { path: '/admin/pagos', label: 'Pagos', icon: '💰', adminOnly: true },
    { path: '/admin/conceptos-pago', label: 'Conceptos de Pago', icon: '🏷️', adminOnly: true },
    { path: '/admin/solicitudes', label: 'Solicitudes', icon: '📋', adminOnly: true },
  ];

  const filteredMenu = menuItems.filter(item => !item.adminOnly || isAdmin());

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-40 h-screen transition-transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} bg-gradient-to-b from-lsfc-blue-700 to-lsfc-blue-900 w-64`}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-4 border-b border-lsfc-blue-600">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">⚽</span>
              </div>
              <div className="min-w-0">
                <h1 className="text-white font-bold text-lg">Los Salias FC</h1>
                <p className="text-lsfc-blue-300 text-xs">Sistema de Gestión</p>
              </div>
            </div>
          </div>

          {/* Menu */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <ul className="space-y-2">
              {filteredMenu.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      (location.pathname === item.path || (item.path !== '/admin' && location.pathname.startsWith(item.path)))
                        ? 'bg-white text-lsfc-blue-700 shadow-lg'
                        : 'text-white hover:bg-lsfc-blue-600'
                    }`}
                  >
                    <span className="text-xl">{item.icon}</span>
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* User info */}
          <div className="p-4 border-t border-lsfc-blue-600">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-lsfc-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">
                  {user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  {user?.email}
                </p>
                <p className="text-lsfc-blue-300 text-xs capitalize">
                  {user?.rol}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowPasswordModal(true);
                setPasswordForm({ actual: '', nueva: '', confirmar: '' });
                setPasswordError('');
                setPasswordSuccess(false);
              }}
              className="w-full px-4 py-2 bg-lsfc-blue-600 text-white rounded-lg hover:bg-lsfc-blue-500 transition-all text-sm mb-2 flex items-center justify-center gap-2"
            >
              🔑 Cambiar Contraseña
            </button>
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all text-sm"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </aside>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">🔑 Cambiar Contraseña</h2>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500"
              >
                ✕
              </button>
            </div>

            {passwordSuccess ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-3xl">✅</span>
                </div>
                <p className="font-medium text-green-700">Contraseña actualizada correctamente</p>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="mt-4 px-6 py-2 bg-lsfc-blue-600 text-white rounded-lg hover:bg-lsfc-blue-700 text-sm"
                >
                  Cerrar
                </button>
              </div>
            ) : (
              <form onSubmit={async (e) => {
                e.preventDefault();
                setPasswordError('');

                if (passwordForm.nueva.length < 6) {
                  setPasswordError('La nueva contraseña debe tener al menos 6 caracteres');
                  return;
                }
                if (passwordForm.nueva !== passwordForm.confirmar) {
                  setPasswordError('Las contraseñas no coinciden');
                  return;
                }

                setChangingPassword(true);
                try {
                  await authAPI.cambiarPassword(passwordForm.actual, passwordForm.nueva);
                  setPasswordSuccess(true);
                } catch (err) {
                  setPasswordError(err.response?.data?.error || 'Error al cambiar contraseña');
                } finally {
                  setChangingPassword(false);
                }
              }} className="space-y-4">
                {passwordError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {passwordError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña actual</label>
                  <input
                    type="password"
                    value={passwordForm.actual}
                    onChange={(e) => setPasswordForm({ ...passwordForm, actual: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lsfc-blue-500 focus:border-transparent"
                    placeholder="••••••••"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nueva contraseña</label>
                  <input
                    type="password"
                    value={passwordForm.nueva}
                    onChange={(e) => setPasswordForm({ ...passwordForm, nueva: e.target.value })}
                    required
                    minLength={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lsfc-blue-500 focus:border-transparent"
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar nueva contraseña</label>
                  <input
                    type="password"
                    value={passwordForm.confirmar}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmar: e.target.value })}
                    required
                    minLength={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lsfc-blue-500 focus:border-transparent"
                    placeholder="Repita la contraseña"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowPasswordModal(false)}
                    className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 text-sm"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={changingPassword}
                    className="flex-1 py-3 bg-lsfc-blue-600 text-white rounded-lg font-medium hover:bg-lsfc-blue-700 disabled:opacity-50 text-sm"
                  >
                    {changingPassword ? 'Guardando...' : 'Cambiar'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="transition-all lg:ml-64">
        {/* Top bar */}
        <header className="bg-white shadow-sm sticky top-0 z-20">
          <div className="flex items-center justify-between px-4 py-3 lg:px-6 lg:py-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div className="flex items-center gap-4">
              <Link
                to="/inscripcion"
                target="_blank"
                className="px-3 py-2 bg-lsfc-green-500 text-white rounded-lg hover:bg-lsfc-green-600 transition-all text-xs sm:text-sm"
              >
                <span className="hidden sm:inline">📝 Ver Formulario Público</span>
                <span className="sm:hidden">📝 Formulario</span>
              </Link>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
